import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useStore } from '../pages/workspace/hooks';
import type { MeasurementData } from '../store/reducers/workspace.reducer';
import { getUserFeatureFlags } from '../store/selectors/featureFlags.selector';
import { getUser } from '../store/selectors/user.selector';
import type { RootState } from '../store/store';
import { FRONTEND_PDF_PLOTS_FLAG } from '../utils/featureFlagKeys';
import { canExportPdf } from '../utils/jobStatus';
import { isFullyCatalogueAssigned } from '../utils/lampMarketMatch';

/** Shared PDF export inputs derived from workspace + feature flags. */
export function usePdfExportInputs() {
  const { project, activeVersion, lamps } = useStore();
  const frontendMeasurementData = useSelector((s: RootState) => s.workspace.frontendMeasurementData);
  const frontendPdfPlotsEnabled = useSelector((s: RootState) =>
    getUserFeatureFlags(s).includes(FRONTEND_PDF_PLOTS_FLAG),
  );
  const user = useSelector(getUser)!;
  const unitSystem = user.settings.units;

  const fullyAssigned = isFullyCatalogueAssigned(activeVersion);
  const measurementData: MeasurementData[] | null =
    (fullyAssigned ? frontendMeasurementData : activeVersion?.measurementData) || null;

  const useFrontendPlots = frontendPdfPlotsEnabled && !!measurementData && measurementData.length > 0;

  const reportImageUrls = useMemo(() => activeVersion?.reportImageUrls ?? {}, [activeVersion?.reportImageUrls]);

  const pdfExportEnabled = canExportPdf(activeVersion, { frontendPdfPlotsEnabled });

  return {
    project,
    activeVersion,
    lamps,
    measurementData,
    reportImageUrls,
    useFrontendPlots,
    pdfExportEnabled,
    unitSystem,
  };
}
